import { ReactFlow, type Node, type Edge } from "@xyflow/react"
import "@xyflow/react/dist/style.css"
import FunnelNode from "./FunnelNode"
import type { FunnelChartData } from "../../types/analytics/ai-report"

const nodeTypes = { funnelNode: FunnelNode }

const fmt = (n: number | undefined | null) => (n ?? 0).toLocaleString("ja-JP")
const signedFmt = (n: number | undefined | null) => { const v = n ?? 0; return `${v >= 0 ? "+" : ""}${fmt(v)}` }

function buildNodes(data: FunnelChartData): Node[] {
  const followersChange = data.action.followers_change ?? 0

  return [
    {
      id: "post",
      type: "funnelNode",
      position: { x: 0, y: 150 },
      data: {
        label: "投稿",
        icon: "bi-pencil-square",
        items: [
          {
            label: "フィード・リール",
            value: `${data.post.feed_count + data.post.reel_count}件`,
            currentRaw: data.post.feed_count + data.post.reel_count,
            prevRaw: (data.post.prev_feed_count ?? 0) + (data.post.prev_reel_count ?? 0),
            diffOnly: true,
          },
          {
            label: "ストーリーズ",
            value: `${data.post.story_count}件`,
            currentRaw: data.post.story_count,
            prevRaw: data.post.prev_story_count ?? 0,
            diffOnly: true,
          },
        ],
      },
    },
    {
      id: "reach",
      type: "funnelNode",
      position: { x: 230, y: 150 },
      data: {
        label: "リーチ",
        icon: "bi-broadcast",
        items: [
          {
            label: "アカウントリーチ",
            value: fmt(data.reach.account_reach),
            currentRaw: data.reach.account_reach,
            prevRaw: data.reach.prev_account_reach,
            diffOnly: true,
          },
          {
            label: "フィード・リール",
            value: fmt(data.reach.feed_reel_content),
            currentRaw: data.reach.feed_reel_content,
            prevRaw: data.reach.prev_feed_reel_content,
          },
          {
            label: "ストーリーズリーチ",
            value: fmt(data.reach.total_story_reach),
            currentRaw: data.reach.total_story_reach,
            prevRaw: data.reach.prev_total_story_reach ?? 0,
            diffOnly: true,
          },
        ],
      },
    },
    {
      id: "engagement",
      type: "funnelNode",
      position: { x: 460, y: 0 },
      data: {
        label: "エンゲージメント",
        icon: "bi-heart",
        isLeaf: true,
        items: [
          {
            label: "インタラクション",
            value: fmt(data.engagement.total_interactions),
            currentRaw: data.engagement.total_interactions,
            prevRaw: data.engagement.prev_total_interactions,
          },
          {
            label: "いいね",
            value: fmt(data.engagement.total_likes),
            currentRaw: data.engagement.total_likes,
            prevRaw: data.engagement.prev_total_likes ?? 0,
            diffOnly: true,
          },
          {
            label: "保存",
            value: fmt(data.engagement.total_saves),
            currentRaw: data.engagement.total_saves,
            prevRaw: data.engagement.prev_total_saves ?? 0,
            diffOnly: true,
          },
          {
            label: "コメント",
            value: fmt(data.engagement.total_comments),
            currentRaw: data.engagement.total_comments,
            prevRaw: data.engagement.prev_total_comments ?? 0,
            diffOnly: true,
          },
        ],
      },
    },
    {
      id: "profile_access",
      type: "funnelNode",
      position: { x: 460, y: 300 },
      data: {
        label: "プロフィールアクセス",
        icon: "bi-person",
        items: [
          {
            label: "アクセス数",
            value: fmt(data.profile_access.total_profile_visits),
            currentRaw: data.profile_access.total_profile_visits,
            prevRaw: data.profile_access.prev_total_profile_visits,
          },
        ],
      },
    },
    {
      id: "url_clicks",
      type: "funnelNode",
      position: { x: 690, y: 240 },
      data: {
        label: "URLクリック",
        icon: "bi-link-45deg",
        variant: "child",
        isLeaf: true,
        items: [
          {
            label: "クリック数",
            value: fmt(data.action.website_clicks),
            currentRaw: data.action.website_clicks,
            prevRaw: data.action.prev_website_clicks,
          },
        ],
      },
    },
    {
      id: "followers",
      type: "funnelNode",
      position: { x: 690, y: 370 },
      data: {
        label: "フォロワー",
        icon: "bi-person-plus",
        variant: "child",
        isLeaf: true,
        items: [
          {
            label: "月末フォロワー",
            value: `${fmt(data.action.followers_count)}人`,
            delta: signedFmt(followersChange),
            deltaPositive: followersChange >= 0,
          },
        ],
      },
    },
  ]
}

const EDGES: Edge[] = [
  { id: "e1", source: "post",           target: "reach",          sourceHandle: "right",  targetHandle: "left", type: "smoothstep", animated: false },
  { id: "e2", source: "reach",          target: "engagement",     sourceHandle: "right",  targetHandle: "left", type: "smoothstep", animated: false },
  { id: "e3", source: "reach",          target: "profile_access", sourceHandle: "right",  targetHandle: "left", type: "smoothstep", animated: false },
  { id: "e4", source: "profile_access", target: "url_clicks",     sourceHandle: "right",  targetHandle: "left", type: "smoothstep", animated: false },
  { id: "e5", source: "profile_access", target: "followers",      sourceHandle: "right",  targetHandle: "left", type: "smoothstep", animated: false },
]

interface FunnelFlowChartProps {
  data: FunnelChartData
}

const FunnelFlowChart = ({ data }: FunnelFlowChartProps) => {
  const nodes = buildNodes(data)

  return (
    <div className="funnel-flow-chart">
      <ReactFlow
        nodes={nodes}
        edges={EDGES}
        nodeTypes={nodeTypes}
        fitView
        fitViewOptions={{ padding: 0.15 }}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={false}
        panOnScroll={false}
        panOnDrag={false}
        zoomOnScroll={false}
        zoomOnDoubleClick={false}
        preventScrolling={false}
        proOptions={{ hideAttribution: true }}
      />
    </div>
  )
}

export default FunnelFlowChart
